import styles from "./Sparkline.module.css";

interface SparklineProps {
  /** Daily values, oldest → newest. Expected length 7. */
  data: number[];
  className?: string;
}

// Geometry — matches the Sparkline spec in docs/mockups/analytics-states.html.
const VIEW_W = 128;
const VIEW_H = 40;
const BAR_W = 14;
const BAR_STEP = 19;
const MAX_BAR_H = 36;
const MIN_BAR_H = 3;

/**
 * Sparkline renders a 7-bar daily mini-chart as a pure inline SVG — no chart
 * library. The tallest bar is highlighted (peak); all-zero data renders as
 * muted minimum-height bars. See docs/mockups/analytics-states.html.
 */
export function Sparkline({ data, className }: SparklineProps) {
  const max = Math.max(0, ...data);
  const allZero = max === 0;
  // First index achieving the max — highlighted as the peak bar.
  const peakIndex = allZero ? -1 : data.indexOf(max);

  return (
    <svg
      className={`${styles.spark}${className ? ` ${className}` : ""}`}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {data.map((v, i) => {
        const h = allZero
          ? MIN_BAR_H
          : Math.max(MIN_BAR_H, (v / max) * MAX_BAR_H);
        let cls = styles.bar;
        if (allZero) cls = styles.empty;
        else if (i === peakIndex) cls = `${styles.bar} ${styles.peak}`;
        return (
          <rect
            key={i}
            className={cls}
            x={i * BAR_STEP}
            y={VIEW_H - h}
            width={BAR_W}
            height={h}
            rx={allZero ? 1.5 : 2}
          />
        );
      })}
    </svg>
  );
}
